import * as THREE from 'three'

import GlassShape from './../glassShape'
import GlassSegment from '@/model/glassSegment'
import MathsUtils from '@/utils/mathsUtils'
import EditorUtils from '@glass-designer/utils/editorUtils'

import Types from '../../types'
import { AlignmentTypes } from '@/store/mathTypes'


/*
 * OvalShape
 * Oval glass shape with width/height
 */
export default class OvalShape extends GlassShape {
  constructor(editor, offsetPosition = null, itemData = {}) {
    super(editor, itemData)
    this.objectClassName = 'OvalShape'
    
    this.shapeType = Types.LibraryItemShapeTypes.oval
    this.width = MathsUtils.roundNumber(parseFloat(itemData.width || 1000), 1)
    this.height = MathsUtils.roundNumber(parseFloat(itemData.height || 750), 1)
    this.segmentListEditable = true

    this.reBuildShape(offsetPosition)
  }


  /**
   * Rebuilds the shape segments based on the basic width/height of the oval
   * Only to be used when first creating the shape
   */
  reBuildShape(offsetPosition) {
    var startX = offsetPosition ? offsetPosition.x : 0.0
    var startY = offsetPosition ? offsetPosition.y : 0.0

    var xRadius = Math.max(this.width / 2, 1)
    var yRadius = Math.max(this.height / 2, 1)
    let center = new THREE.Vector2(startX + xRadius, startY + yRadius)

    this.segmentList = EditorUtils.generateSegmentsForOval(center, this.width, this.height)
  }


  /**
   * Resize the shape
   * Handled by the actual shape classes to rebuild the shape
   */
  resizeShape(width, height) {
    let bounds = this.calculateSegmentBounds()
    if(bounds.isEmpty())
      return

    this.width = width
    this.height = height

    this.reBuildShape(bounds.min)    
  }


  /**
   * Calculates the default panel item position 2D coordinate 
   * Used for panel items automatically added to the shape via applyDefaultPanelItems
   * Returns 2D position of the center of the panel item
   */
  calculateDefaultPanelItemPosition(alignHorz, alignVert, itemWidth, itemHeight) {
    let shapeBounds = this.calculateSegmentBounds()
    let center = shapeBounds.getCenter(new THREE.Vector2())
    let posDir = new THREE.Vector2(0, 0)

    switch(alignHorz) {
      case AlignmentTypes.left:
        posDir.x = -1
        break
      case AlignmentTypes.center:
        posDir.x = 0
        break
      case AlignmentTypes.right:
        posDir.x = 1
        break
    }
    switch(alignVert) {
      case AlignmentTypes.top:
        posDir.y = -1
        break
      case AlignmentTypes.middle:
        posDir.y = 0
        break
      case AlignmentTypes.bottom:
        posDir.y = 1
        break
    }

    posDir.normalize()
    let angle = MathsUtils.get2DVectorAngle(posDir)

    return center.add(MathsUtils.generate2DDirectionVector(angle).multiply(new THREE.Vector2(this.width/2 - itemWidth/2, this.height/2 - itemHeight/2)))
  }


  // Clone the oval shape
  clone() {
    let newShape = new OvalShape(this.editor, null, this.itemData)

    newShape.copy(this)
    return newShape
  }

  // Copy the oval shape to this shape
  copy(otherShape) {
    super.copy(otherShape)
    
    this.width = otherShape.width
    this.height = otherShape.height
  }

  // Serialize/Deserialize to/from a JSON object
  serialize() {
    var outputData = super.serialize()

    outputData.width = this.width
    outputData.height = this.height

    return outputData
  }
  deserialize(object) {
    super.deserialize(object)

    this.width = parseFloat(object.width || 0)
    this.height = parseFloat(object.height || 0)    
  }


  /**
   * Check if the layout is still a circle shape
   * Used for special resizing of specific shapes
   */
  isLayoutOvalShape() {
    // Check check if the segment list is all arcs
    // Not 100% accurate, but good enough
    return this.segmentList.every(segment => segment.type == Types.SegmentTypes.arc)
  }
}
